import { useAuth } from "@/contexts/AuthContext";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    LayoutDashboard,
    Inbox,
    Users,
    FileText,
    BarChart3,
    LogOut,
    Stethoscope,
    Menu as MenuIcon,
    X,
    Settings as SettingsIcon,
    MoreHorizontal,
} from "lucide-react";

const NAV = [
    { label: "Дашборд", to: "/", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
    { label: "Обращения", to: "/appeals", icon: Inbox, testId: "nav-appeals" },
    { label: "Администраторы", to: "/admins", icon: Users, testId: "nav-admins" },
    { label: "Контент бота", to: "/content", icon: FileText, testId: "nav-content" },
    { label: "Аналитика", to: "/analytics", icon: BarChart3, testId: "nav-analytics" },
    { label: "Настройки", to: "/settings", icon: SettingsIcon, testId: "nav-settings" },
];

// Bottom-tab items on mobile (max 5, most-used first)
const MOBILE_TABS = [
    { label: "Обращения", to: "/appeals", icon: Inbox, testId: "tab-appeals" },
    { label: "Дашборд", to: "/", icon: LayoutDashboard, end: true, testId: "tab-dashboard" },
    { label: "Контент", to: "/content", icon: FileText, testId: "tab-content" },
    { label: "Аналитика", to: "/analytics", icon: BarChart3, testId: "tab-analytics" },
];

const ROLE_LABELS = {
    chairman: "Председатель",
    head: "Зав. отделением",
    staff: "Сотрудник",
};

const PAGE_TITLE = {
    "/": "Дашборд",
    "/appeals": "Обращения",
    "/admins": "Администраторы",
    "/content": "Контент бота",
    "/analytics": "Аналитика",
    "/settings": "Настройки",
};

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const SidebarContent = (
        <>
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-brand text-white flex items-center justify-center shrink-0">
                        <Stethoscope className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-semibold text-slate-900 text-sm">БГМК · 2026</div>
                        <div className="text-xs text-slate-500">Приёмная комиссия</div>
                    </div>
                </div>
                <button
                    onClick={() => setDrawerOpen(false)}
                    className="lg:hidden p-1.5 -mr-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                    data-testid="sidebar-close"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {NAV.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={item.testId}
                        className={({ isActive }) =>
                            [
                                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                                isActive
                                    ? "bg-slate-100 text-slate-900"
                                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                            ].join(" ")
                        }
                    >
                        <item.icon className="h-4 w-4" strokeWidth={1.5} />
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            <div className="p-3 border-t border-slate-200">
                {user && (
                    <div className="px-3 py-2 mb-2">
                        <div
                            className="text-sm font-medium text-slate-800 truncate"
                            data-testid="sidebar-user-name"
                        >
                            {user.name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                            {ROLE_LABELS[user.role] || user.role}
                        </div>
                    </div>
                )}
                <button
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                >
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    <span>Выход</span>
                </button>
            </div>
        </>
    );

    const currentTitle = PAGE_TITLE[location.pathname] || "Кабинет";

    return (
        <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
            {/* Mobile top bar */}
            <header
                className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4"
                data-testid="mobile-topbar"
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setDrawerOpen(true)}
                        data-testid="mobile-menu-toggle"
                        className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700"
                    >
                        <MenuIcon className="h-5 w-5" />
                    </button>
                    <div className="font-heading font-medium text-slate-900">{currentTitle}</div>
                </div>
                <div className="h-8 w-8 rounded-md bg-brand text-white flex items-center justify-center">
                    <Stethoscope className="h-4 w-4" strokeWidth={1.5} />
                </div>
            </header>

            {/* Desktop sidebar */}
            <aside
                className="hidden lg:flex w-64 shrink-0 border-r border-slate-200 bg-white flex-col"
                data-testid="sidebar"
            >
                {SidebarContent}
            </aside>

            {/* Mobile drawer */}
            {drawerOpen && (
                <>
                    <div
                        className="lg:hidden fixed inset-0 bg-slate-900/40 z-40 animate-fade-in"
                        onClick={() => setDrawerOpen(false)}
                        data-testid="mobile-drawer-overlay"
                    />
                    <aside
                        className="lg:hidden fixed inset-y-0 left-0 w-72 bg-white z-50 flex flex-col shadow-xl animate-fade-in"
                        data-testid="mobile-drawer"
                    >
                        {SidebarContent}
                    </aside>
                </>
            )}

            {/* Main */}
            <main className="flex-1 min-w-0 overflow-auto pt-14 lg:pt-0 pb-16 lg:pb-0">
                <Outlet />
            </main>

            {/* Bottom tab bar (mobile only) */}
            <nav
                className="lg:hidden fixed bottom-0 inset-x-0 z-30 h-16 bg-white border-t border-slate-200 flex items-stretch justify-around shadow-[0_-2px_10px_rgba(0,0,0,0.04)] pb-[env(safe-area-inset-bottom)]"
                data-testid="mobile-bottom-tabs"
            >
                {MOBILE_TABS.map((t) => (
                    <NavLink
                        key={t.to}
                        to={t.to}
                        end={t.end}
                        data-testid={t.testId}
                        className={({ isActive }) =>
                            [
                                "flex-1 flex flex-col items-center justify-center gap-0.5 min-w-0 transition-colors",
                                isActive ? "text-brand" : "text-slate-500 hover:text-slate-800 active:text-slate-800",
                            ].join(" ")
                        }
                    >
                        <t.icon className="h-5 w-5" strokeWidth={1.75} />
                        <span className="text-[10px] font-medium leading-none truncate max-w-full px-1">{t.label}</span>
                    </NavLink>
                ))}
                <button
                    onClick={() => setDrawerOpen(true)}
                    data-testid="tab-more"
                    className="flex-1 flex flex-col items-center justify-center gap-0.5 min-w-0 text-slate-500 hover:text-slate-800 active:text-slate-800 transition-colors"
                >
                    <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />
                    <span className="text-[10px] font-medium leading-none">Ещё</span>
                </button>
            </nav>
        </div>
    );
}
